import { useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { SchemaForm } from '@/shared/ui/SchemaForm';
import { logger } from '@/lib/utils/logger';
import type { InputSchemaProps } from './InputSchema.types';

const ErrorFallback = ({ error }: { error: Error }) => {
  const { t } = useTranslation('admin');

  logger.error('Error: Invalid schema', error);
  return (
    <p className="text-sm text-destructive" data-testid="input-schema-error-fallback">
      {t('agents.inputSchema.errorAgentSchemaIsInvalid')}
    </p>
  );
};

/**
 * Preview of an agent's input schema. The values are local to the preview — nothing
 * is submitted anywhere — so the form owns its own data.
 */
export const InputSchema = ({ schema }: InputSchemaProps) => (
  <ErrorBoundary FallbackComponent={ErrorFallback}>
    <SchemaPreview schema={schema} />
  </ErrorBoundary>
);

function SchemaPreview({ schema }: InputSchemaProps) {
  const [formData, setFormData] = useState<Record<string, unknown>>({});

  // The schema arrives untyped from the API, so a malformed one has to reach the error
  // boundary rather than render an empty form that reads as "this agent takes no input".
  const raw: unknown = schema;
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    throw new Error('Agent input schema is not a JSON Schema object');
  }

  return (
    <div className="flex w-full flex-row gap-4">
      <SchemaForm
        schema={schema}
        formData={formData}
        onChange={setFormData}
        idPrefix="agent-input-schema"
      />
    </div>
  );
}
